import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import api from "../../globals/axios";
import {
  Container,
  Typography,
  TextField,
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Checkbox,
  ListItemText,
  OutlinedInput,
  Box,
} from "@mui/material";
import CustomAppBar from "../../components/AppBar";
import { CustomTitle } from "../../components/Custom/CustomTypography";
import { useTranslation } from "react-i18next";
import GenericButton from "../../components/Custom/GenericButton";
import LoaderContainer from "../../components/LoaderContainer";

const defaultCenter = {
  lat: 20.5937,
  lng: 78.9629,
};

const serviceTypes = [
  { value: "input_supply", label: "Input Supply" },
  { value: "machines", label: "Machines" },
  { value: "warehouses", label: "Warehouses" },
  { value: "mills", label: "Mills" },
];

const ServiceProviderDetails = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    service_type: "",
    location: { lat: "", lng: "" },
    crops_covered: [],
    quantity_coverage: "",
  });
  const [commodityList, setCommodityList] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [center, setCenter] = useState(defaultCenter);
  console.log(center);

  useEffect(() => {
    const loadData = async () => {
      await fetchCropsList();
      getUserLocation();
    };
    loadData();
  }, []);

  useEffect(() => {
    if (commodityList.length > 0) {
      fetchServiceProviderDetails();
    }
  }, [commodityList]);

  const fetchServiceProviderDetails = async () => {
    try {
      const response = await api.get(
        "/serviceprovider/serviceproviderdetails/"
      );
      if (response.data && response.data.length > 0) {
        const detail = response.data[0];
        const locationMatch = detail.location.match(
          /POINT \(([^ ]+) ([^ ]+)\)/
        );
        const lat = parseFloat(locationMatch[2]);
        const lng = parseFloat(locationMatch[1]);

        // Map crop IDs to names
        const cropNames = commodityList
          .filter((commodity) => detail.crops_covered.includes(commodity.id))
          .map((commodity) => commodity.name);

        setFormData({
          id: detail.id,
          service_type: detail.service_type,
          location: { lat, lng },
          crops_covered: cropNames,
          quantity_coverage: detail.quantity_coverage,
        });
        setCenter({ lat, lng });
        setIsEditing(false);
      } else {
        setIsEditing(true);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching service provider details:", error);
    }
  };

  const fetchCropsList = async () => {
    try {
      const response = await api.get("/common/commodities/");
      setCommodityList(response.data);
    } catch (error) {
      console.log("Error Fetching Commodities list:", error);
    }
  };

  const getUserLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setCenter({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
          setFormData((prevData) => ({
            ...prevData,
            location: {
              lat: position.coords.latitude,
              lng: position.coords.longitude,
            },
          }));
        },
        (error) => {
          console.error("Error getting user location:", error);
        },
        {
          enableHighAccuracy: true,
          timeout: 5000,
          maximumAge: 0,
        }
      );
    } else {
      console.error("Geolocation is not supported by this browser.");
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCropsChange = (event) => {
    setFormData({
      ...formData,
      crops_covered: event.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const cropsIds = formData.crops_covered.map(
        (cropName) =>
          commodityList.find((commodity) => commodity.name === cropName)?.id ||
          ""
      );

      const dataToSend = { ...formData, crops_covered: cropsIds };
      console.log("Submitting data:", dataToSend);

      if (isEditing) {
        const response = await api.post(
          "/serviceprovider/serviceproviderdetails/",
          dataToSend
        );
        console.log("Create response:", response.data);
      } else {
        const response = await api.put(
          `/serviceprovider/serviceproviderdetails/${formData.id}/`,
          dataToSend
        );
        console.log("Update response:", response.data);
      }

      fetchServiceProviderDetails();
    } catch (error) {
      console.error("Error saving service provider details:", error);
    }
  };

  return (
    <>
      <Helmet>
        <title>Add Service - Farmapp</title>
      </Helmet>
      <CustomAppBar />
      <LoaderContainer loading={loading}>
        <Container>
          <CustomTitle>{t("Add Service")}</CustomTitle>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <form onSubmit={handleSubmit}>
                <FormControl fullWidth margin="normal">
                  <InputLabel>Service Type</InputLabel>
                  <Select
                    name="service_type"
                    value={formData.service_type}
                    onChange={handleInputChange}
                    required
                    disabled={!isEditing} // Disable if not editing
                  >
                    {serviceTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Typography variant="subtitle1" gutterBottom>
                  Select Location on Map
                </Typography>

                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    height: "400px",
                    pointerEvents: isEditing ? "auto" : "none", // Disable interactions if not editing
                  }}
                >
                  {/* <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    zoom={16}
                    center={center}
                    onClick={handleMapClick}
                  >
                    {formData.location.lat && (
                      <Marker
                        position={{
                          lat: formData.location.lat,
                          lng: formData.location.lng,
                        }}
                      />
                    )}
                  </GoogleMap> */}

                  {!isEditing && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        height: "100%",
                        backgroundColor: "rgba(255, 255, 255, 0.5)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "gray",
                        fontSize: "16px",
                        fontWeight: "bold",
                      }}
                    >
                      Map is disabled
                    </Box>
                  )}
                </Box>

                <FormControl fullWidth margin="normal">
                  <InputLabel>Crops Covered</InputLabel>
                  <Select
                    multiple
                    value={formData.crops_covered}
                    onChange={handleCropsChange}
                    input={<OutlinedInput />}
                    renderValue={(selected) => selected.join(", ")}
                    disabled={!isEditing} // Disable if not editing
                  >
                    {commodityList.map((commodity) => (
                      <MenuItem key={commodity.id} value={commodity.name}>
                        <Checkbox
                          checked={
                            formData.crops_covered.indexOf(commodity.name) > -1
                          }
                        />
                        <ListItemText primary={commodity.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  name="quantity_coverage"
                  label="Quantity Coverage"
                  value={formData.quantity_coverage}
                  onChange={handleInputChange}
                  fullWidth
                  margin="normal"
                  required
                  disabled={!isEditing} // Disable if not editing
                />

                {isEditing && (
                  <GenericButton
                    onClick={handleSubmit}
                    backgroundColor="#4CAF50"
                    fontColor="white"
                    padding="8px 0"
                  >
                    {t("Add Details Possible")}
                  </GenericButton>
                )}
              </form>
            </Grid>
          </Grid>
        </Container>
      </LoaderContainer>
    </>
  );
};

export default ServiceProviderDetails;
